import React from "react";
import { Link } from "react-router-dom";
import { HardHat, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 10, 2026";
const COMPANY_NAME   = "Builders Binder";
const CONTACT_EMAIL  = "support@buildersbinder.com";
const SITE_URL       = "https://buildersbinder.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white font-sans text-zinc-800">
      {/* Nav */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-500 rounded-sm flex items-center justify-center">
              <HardHat size={15} className="text-zinc-950" />
            </div>
            <span className="font-bold text-sm tracking-tight">{COMPANY_NAME}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Legal</p>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Privacy Policy</h1>
          <p className="text-sm text-zinc-500">Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}</p>
        </div>

        <div className="prose prose-zinc max-w-none space-y-8 text-[15px] leading-relaxed">

          <section>
            <p>
              {COMPANY_NAME} ("<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>") operates{" "}
              <a href={SITE_URL} className="underline text-zinc-700">{SITE_URL}</a> and related services (collectively, the
              "<strong>Service</strong>"). This Privacy Policy explains what personal information we collect, how we
              use it, and the choices you have. By using the Service, you agree to this Privacy Policy. If you do not
              agree, do not use the Service.
            </p>
          </section>

          <Section title="1. Information We Collect">
            <Subsection title="1.1 Information You Provide Directly">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account registration:</strong> name, email address, and password (stored as a salted bcrypt hash — we never store plaintext passwords).</li>
                <li><strong>Profile information:</strong> company name and US phone number (optional account contact information).</li>
                <li><strong>Tracked job information:</strong> job nicknames, addresses, boroughs, ZIP codes, BINs, BBLs, block/lot identifiers, selected DOB job filing numbers, permits, inspections, violations, and manual entries you add.</li>
                <li><strong>Alert settings:</strong> notification preferences, trigger preferences, alert history, and test alert events.</li>
                <li><strong>Payment information:</strong> billing details such as credit card number, billing address, and payment method are collected and stored solely by our payment processor, <strong>Paddle.com Market Limited</strong>. We receive only non-sensitive metadata (plan ID, last-four digits, billing period) from Paddle. We never store raw card data on our servers.</li>
                <li><strong>Communications:</strong> messages you send to our support team.</li>
              </ul>
            </Subsection>

            <Subsection title="1.2 Information We Collect Automatically">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Log and security data:</strong> IP address, browser type, pages visited, timestamp, referring URL, request path, rate-limit events, authentication events, and error events, collected automatically by our servers and error-monitoring tools.</li>
                <li><strong>Cookies and similar technologies:</strong> a session token stored in an <code className="text-xs bg-zinc-100 px-1 rounded">httpOnly</code> cookie to keep you signed in. We do not use advertising or cross-site tracking cookies.</li>
                <li><strong>Usage data:</strong> aggregate, non-personally-identifiable analytics (e.g., feature usage frequency) to improve the Service.</li>
              </ul>
            </Subsection>

            <Subsection title="1.3 Third-Party Sign-In (Google OAuth)">
              <p>
                If you sign in with Google, we receive your name, email address, and profile picture from Google. We
                do not receive or store your Google password. Your email and name are used to create or locate your
                account. You can manage Google's sharing of this data at{" "}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="underline text-zinc-700">
                  myaccount.google.com/permissions
                </a>.
              </p>
            </Subsection>

            <Subsection title="1.4 Public Data We Retrieve on Your Behalf">
              <p>
                The Service retrieves publicly available address, BIN, BBL, permit, job filing, inspection, DOB
                violation, and ECB violation records from New York City public data sources, including DOB NOW,
                NYC Open Data APIs, NYC GeoSearch, and related public records, using job addresses, BINs, BBLs, or
                job numbers you provide or select. This data originates from government or public-record sources and
                is not personal information about you unless you choose to associate it with your account.
                <strong> Public-record information may be delayed, incomplete, duplicated, corrected, or inconsistent
                across government systems.</strong> Always verify critical permit, filing, violation, payment, and
                inspection information directly with the NYC Department of Buildings at{" "}
                <a href="https://www.nyc.gov/site/buildings/index.page" target="_blank" rel="noopener noreferrer" className="underline text-zinc-700">nyc.gov/buildings</a>.
                We make no representations regarding the accuracy, completeness, or timeliness of data retrieved
                from third-party government sources.
              </p>
            </Subsection>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Provide and operate the Service:</strong> authenticate your account, resolve addresses, display permit, filing, inspection, and violation data, save selected DOB job filings, enforce plan limits, and send alerts.</li>
              <li><strong>Process payments:</strong> communicate with Paddle to initiate subscription checkout and the billing portal.</li>
              <li><strong>Send transactional communications:</strong> verification codes for email, phone, and password changes; permit status alerts; filing alerts; violation alerts; inspection reminders; test alerts; and account notices you have opted into or that are necessary to operate the Service.</li>
              <li><strong>Customer support:</strong> respond to your inquiries and troubleshoot issues.</li>
              <li><strong>Security and fraud prevention:</strong> detect abuse, unauthorized access, and enforce rate limits.</li>
              <li><strong>Legal compliance:</strong> comply with applicable law, enforce our Terms of Service, and protect our rights.</li>
              <li><strong>Service improvement:</strong> analyze aggregated usage patterns to improve features. We do not train machine-learning models on your personal data.</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell, rent, or share your personal information with third parties for their own
              marketing or advertising purposes.
            </p>
          </Section>

          <Section title="3. Legal Bases for Processing (EEA / UK Users)">
            <p>If you are located in the European Economic Area or United Kingdom, we process your data under the following legal bases:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Contract performance:</strong> to provide the Service you signed up for.</li>
              <li><strong>Legitimate interests:</strong> security, fraud prevention, and service improvement, where those interests are not overridden by your rights.</li>
              <li><strong>Legal obligation:</strong> to comply with applicable law.</li>
              <li><strong>Consent:</strong> for any processing activity where we have explicitly asked for your consent.</li>
            </ul>
          </Section>

          <Section title="4. Information Sharing and Disclosure">
            <p>We share personal information only in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Service providers:</strong> Paddle (payments), transactional email providers, cloud hosting, and error-monitoring services — all bound by data-processing agreements and permitted to use your data only to perform services for us.</li>
              <li><strong>Additional operational providers:</strong> address/geosearch providers, database providers, hosting providers, and public-record API providers may receive the limited information needed to resolve addresses, retrieve public records, deliver alerts, secure the Service, and operate the dashboard.</li>
              <li><strong>Legal requirements:</strong> if required by law, subpoena, or similar legal process, or if we believe in good faith that disclosure is necessary to protect rights, safety, or property.</li>
              <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, in which case the successor entity will be bound by this Privacy Policy or will provide you with notice and choice.</li>
              <li><strong>With your consent:</strong> for any other purpose, only with your explicit consent.</li>
            </ul>
          </Section>

          <Section title="5. Data Retention">
            <p>
              We retain your personal information for as long as your account is active or as needed to provide the
              Service. If you delete your account, we will delete or anonymize your personal data within 30 days,
              except where retention is required by law (e.g., financial records required for tax purposes, retained
              for up to 7 years). Log data is retained for up to 90 days for security purposes.
            </p>
          </Section>

          <Section title="6. Security">
            <p>
              We implement industry-standard safeguards including TLS encryption in transit, bcrypt password hashing,
              httpOnly session cookies, and rate limiting on sensitive endpoints. No method of transmission or storage
              is 100% secure. If you discover a security vulnerability, please report it to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a>.
            </p>
          </Section>

          <Section title="7. Your Rights and Choices">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Access and correction:</strong> you can view and update your name, company, and phone number at any time in Account Settings. If you signed in with email/password, you can also update your email and password there.</li>
              <li><strong>Deletion:</strong> you may request deletion of your account by emailing us at <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a>.</li>
              <li><strong>Data portability:</strong> you may request a copy of your personal data in a machine-readable format.</li>
              <li><strong>Opt-out of communications:</strong> you can disable email alerts in Alert Settings. You cannot opt out of account-related transactional emails (e.g., verification codes) while your account is active.</li>
              <li><strong>EEA / UK residents:</strong> you have the right to object to processing, request restriction, and lodge a complaint with your local supervisory authority.</li>
              <li><strong>California residents (CCPA):</strong> you have the right to know what personal information we collect, to request deletion, and to opt out of sale. We do not sell personal information.</li>
            </ul>
            <p className="mt-3">To exercise any of the above rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a>. We will respond within 30 days.</p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              The Service is intended for business users and is not directed to anyone under the age of 18. We do not
              knowingly collect personal information from minors. If you believe a minor has provided us with personal
              information, contact us and we will delete it promptly.
            </p>
          </Section>

          <Section title="9. Third-Party Links">
            <p>
              The Service may contain links to third-party websites (e.g., NYC Open Data, Paddle). This Privacy Policy
              does not apply to those sites. We encourage you to review their privacy policies before sharing personal
              information.
            </p>
          </Section>

          <Section title="10. International Data Transfers">
            <p>
              Our servers are located in the United States. If you access the Service from outside the United States,
              your information will be transferred to and processed in the United States, which may have different
              data-protection laws than your country. By using the Service, you consent to this transfer. For EEA/UK
              users, we rely on Standard Contractual Clauses or other appropriate mechanisms where required.
            </p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the "Effective date" at
              the top and, for material changes, notify you by email or by a prominent notice within the Service at
              least 14 days before the change takes effect. Continued use of the Service after the effective date
              constitutes acceptance of the updated Policy.
            </p>
          </Section>

          <Section title="12. Notification Delivery Disclaimer">
            <p>
              The Service sends permit and inspection alerts via email on a best-effort basis. Delivery
              depends on third-party email providers whose performance we do not control.
              We do not guarantee that notifications will be delivered promptly, completely, or at all.
            </p>
            <p className="mt-2">
              <strong>Notifications should not be relied upon as the sole source of permit or inspection
              information.</strong> Factors including spam filtering, carrier restrictions, account configuration
              errors, or infrastructure outages may prevent or delay delivery without notice. You remain
              responsible for monitoring permit and inspection activity through official NYC DOB channels
              independently of our notification service.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>Questions about this Privacy Policy or our data practices? Contact us at:</p>
            <address className="not-italic mt-2 text-sm bg-zinc-50 border border-zinc-200 rounded-sm p-4">
              <strong>{COMPANY_NAME}</strong><br />
              Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a><br />
              Website: <a href={SITE_URL} className="underline text-zinc-700">{SITE_URL}</a>
            </address>
          </Section>

        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-12 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
          <nav className="flex gap-5">
            <Link to="/privacy" className="hover:text-zinc-950 underline underline-offset-2">Privacy Policy</Link>
            <Link to="/terms"   className="hover:text-zinc-950 underline underline-offset-2">Terms of Service</Link>
            <Link to="/refunds" className="hover:text-zinc-950 underline underline-offset-2">Refund Policy</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-zinc-950">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-bold tracking-tight text-zinc-950 border-b border-zinc-100 pb-2">{title}</h2>
      {children}
    </section>
  );
}

function Subsection({ title, children }) {
  return (
    <div className="mt-3 space-y-2">
      <h3 className="text-base font-semibold text-zinc-800">{title}</h3>
      {children}
    </div>
  );
}
